/**
 * Whose exercise a row is, as a 28px circle.
 *
 * Owner decision D5, 17 September 2026: a library movement carries the **original MoveBuddy mark**,
 * never a letter, on an INK circle rather than an accent one — the accent on this screen is the
 * header's `+` and nothing else. The trainer's own movements carry the trainer.
 *
 * ── The mark is MASKED, not drawn ───────────────────────────────────────────────────────────
 * `shared/ui/brandMark.module.css` paints `--text` through `public/brand/mark.png`, which is how
 * every other surface in this app draws it. Here the circle is already `--text`, so the mask has to
 * paint the BACKGROUND colour instead — the mark is a hole in the disc rather than ink on it. That
 * is one declaration this file owns and the shared module cannot: `brandMark` states the ink, and a
 * second stylesheet restating it would be settled by chunk order.
 *
 * ── The trainer is `shared/ui/AvatarChip` at 28 ─────────────────────────────────────────────
 * Its own photograph, or the two letters of their name. `--sz-avatar` is the chip's own size hook,
 * so the geometry is stated once here and never inside the shared component.
 */

import { useTranslation } from 'react-i18next';

import { AvatarChip } from '~/shared/ui/AvatarChip';

import type { ExerciseAuthor } from '../model/exerciseRow';

import brandMark from '~/shared/ui/brandMark.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import styles from './ExerciseTable.module.css';

interface ExerciseAuthorMarkProps {
  readonly author: ExerciseAuthor;
  /** The trainer's two letters. Drawn only on their own movements. */
  readonly initials: string;
  readonly avatarUrl: string | null;
}

export function ExerciseAuthorMark({ author, initials, avatarUrl }: ExerciseAuthorMarkProps) {
  const { t } = useTranslation();

  if (author === 'own') {
    return (
      <span className={styles.authorOwn}>
        <AvatarChip initials={initials} avatarUrl={avatarUrl} variant="library" loading="lazy" />
        {/* `AvatarChip` is `aria-hidden` by construction — it is a face, not a name — so the
          * column's meaning is said here once. */}
        <span className={hidden.srOnly}>{t('web:exerciseLibrary.row.authorOwn')}</span>
      </span>
    );
  }

  return (
    <span className={styles.authorLibrary}>
      <span className={`${String(brandMark.brandMark)} ${String(styles.authorMark)}`} aria-hidden />
      <span className={hidden.srOnly}>{t('web:exerciseLibrary.row.authorLibrary')}</span>
    </span>
  );
}
